'use client';
import React, { useEffect, useRef, useState } from 'react';
import PaginationButtons from 'src/components/shared/components/pagination-buttons';

import cn from '@/lib/clsxm';

import Container from '@/components/@base/container';
import { useClientFilterParams } from '@/components/app/archive/helpers/hooks';
import Section from '@/components/app/product/components/@shared/section';
import Filter from '@/components/app/product/components/comments/filter';
import LoginAlert from '@/components/app/product/components/comments/login-alert';
import AddQuestionBox from '@/components/app/product/components/questions/add-question';
import QuestionCard from '@/components/app/product/components/questions/question-card';
import Loading from '@/components/app/product/components/questions/question-card/loading';
import QuestionHeader from '@/components/app/product/components/questions/question-header';
import Props from '@/components/app/product/components/questions/type';
import { useGetQuestionList } from '@/components/app/product/services/hooks';
import { useGetProfile } from '@/components/layout/services/hooks';
import { loadingGenerator } from '@/components/shared/helpers';
import useScrollTo from '@/components/shared/helpers/hooks/use-scroll-to';

import { DATA_CY_PRODUCT_QUESTIONS } from '@/constant/cypress';
import QuestionModel from '@/models/question.model';

export default function Questions({ slug, rate, productId, questionBoxRef }: Props) {
  const { filterParams, updateParams } = useClientFilterParams();
  const scrollToNode = useScrollTo();
  const headerRef = useRef<HTMLDivElement>(null);
  const replyParentRefs = useRef(new Map());
  const [questionList, setQuestionList] = useState<QuestionModel[]>([]);
  const [replyParentId, setReplyParentId] = useState<string>();

  const { profile, isAuthenticated } = useGetProfile();
  const { data, refetch, isPending } = useGetQuestionList({
    slug,
    filters: filterParams,
    page: filterParams['page'] ?? 1,
  });

  useEffect(() => {
    if (!data) {
      return;
    }
    setQuestionList(data.questions);
  }, [data]);

  useEffect(() => {
    refetch();
  }, [filterParams['page'], filterParams['badge']]);

  const handleAddQuestionClick = () => {
    setReplyParentId(undefined);
    scrollToNode(questionBoxRef.current, 200, 'textarea');
  };

  const handleReplyClick = (id: string) => {
    if (isAuthenticated) {
      setReplyParentId(id);
    } else {
      scrollToNode(questionBoxRef.current, 200, 'textarea');
    }
  };

  const handlePageChange = (page: number) => {
    updateParams('page', page, false, { scroll: false });
    scrollToNode(headerRef.current, 80);
  };

  const handleUpdateQuestionList = (newQuestion: QuestionModel) => {
    let commentIndex = questionList.findIndex((item) => item.id === newQuestion.id);
    if (commentIndex >= 0) {
      const newQuestionList = questionList.map((comment) =>
        comment.id === newQuestion.id ? newQuestion : comment,
      );
      setQuestionList(newQuestionList);
    } else {
      setQuestionList([...questionList, newQuestion]);
    }
  };

  return (
    <>
      <Section className='mx-0! w-full p-0!' data-cy={DATA_CY_PRODUCT_QUESTIONS}>
        <QuestionHeader
          rate={rate}
          className='m-0! w-full px-3.5 py-[15px] md:px-[30px] md:pt-8 md:pb-5'
          onAddQuestionClick={handleAddQuestionClick}
          ref={headerRef}
        />

        <Container className='border-t-[1px] border-t-[#F4F5F6] px-3.5 py-[15px] pt-0! md:px-[30px]'>
          {questionList.length > 0 && (
            <Container className='mt-0 mb-0!'>
              <Filter />
            </Container>
          )}

          {isPending ? (
            loadingGenerator(12, () => <Loading />)
          ) : (
            <>
              {questionList.length > 0 &&
                questionList.map((question) => (
                  <Container
                    key={question.id}
                    className='mb-5 overflow-hidden rounded-lg border-[1px] border-[#E9EBF1] p-[15px] last:mb-0'
                  >
                    <QuestionCard
                      {...question}
                      onReplyClick={handleReplyClick}
                      currentUser={profile}
                      slug={slug}
                      productId={productId}
                      onUpdateQuestionList={handleUpdateQuestionList}
                      ref={(node) => {
                        node
                          ? replyParentRefs.current.set(question.id, node)
                          : replyParentRefs.current.delete(question.id);
                      }}
                    />
                  </Container>
                ))}

              {data && data.totalPages !== null && data.totalPages > 1 && (
                <PaginationButtons
                  totalPages={data?.totalPages ?? undefined}
                  currentPage={data.currentPage ?? undefined}
                  onPageChange={handlePageChange}
                  className='my-4'
                />
              )}
            </>
          )}
        </Container>

        {isAuthenticated && profile && (
          <Container
            className={cn(
              questionList.length > 0 && 'border-t-[1px] border-t-[#F4F5F6]',
              'px-3.5 pb-[15px] md:px-[30px] md:pb-9',
            )}
          >
            <AddQuestionBox
              ref={questionBoxRef}
              user={profile}
              slug={slug}
              productId={productId}
              onUpdateQuestionList={handleUpdateQuestionList}
              parentId={replyParentId}
            />
          </Container>
        )}
      </Section>
      {!isAuthenticated && <LoginAlert ref={questionBoxRef} />}
    </>
  );
}
